import { useState } from "react";
import { Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { stockWriteOffsApi, type StockWriteOffListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];
const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/**
 * UC-M1-INV-05 — Stock write-off queue. Negative deltas (Expired / Damaged / Lost / QC Reject)
 * write WriteOff ledger rows on post.
 */
export function StockWriteOffsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("inventory.writeoff.create"));

  const query = useQuery({
    queryKey: ["stock-writeoffs", status, pagination.page, pagination.pageSize],
    queryFn: () => stockWriteOffsApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<StockWriteOffListDto>[] = [
    { field: "documentNo", headerName: "Write-off #", width: 160 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "" },
    { field: "warehouseName", headerName: "Warehouse", flex: 1, minWidth: 160 },
    { field: "reason", headerName: "Reason", flex: 1, minWidth: 200 },
    { field: "status", headerName: "Status", width: 120,
      renderCell: (p) => <Chip size="small" label={p.value} color={STATUS_COLOR[p.value as string] ?? "default"} /> },
    { field: "lineCount", headerName: "Lines", width: 80, type: "number" },
    { field: "totalQty", headerName: "Total qty", width: 110, type: "number" },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Stock write-offs</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate("/inventory/writeoffs/new")}>
            New write-off
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}
            size="small" sx={{ minWidth: 160 }}>
            {STATUS.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => navigate(`/inventory/writeoffs/${p.id}`)}
            sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          />
        </Box>
      </Paper>
    </Box>
  );
}
